import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { RadioButtonEventValue } from '@interstate/components/RadioButtonList';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { paymentActionCreators } from '@makemydeal/dr-offer-redux';
import { LEASE, OfferTypeEnum, PaymentScheduleTypes } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { kebabCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { PAYMENT_FREQUENCY, PAYMENT_FREQUENCY_OPTIONS } from '../../../constants';
import { dealLockHooks, usePaymentFrequency } from '../../../hooks';

const { programQuotes } = CALC_ENDPOINTS;

export const PAYMENT_FREQUENCY_LABEL = 'Payment Frequency';

export const usePaymentFrequencyField = () => {
    const dispatch = useDispatch();
    const { paymentFrequency } = usePaymentFrequency();
    const isLocked = dealLockHooks.useIsDealLocked();
    const paymentGridEnabled: boolean = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, OfferTypeEnum.LEASE)
    );
    const currentCalcEndpoint = useSelector((state: StateTree) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, offerReduxSelectors.getCurrentOfferType(state))
    );
    const isOPLAutoSelectLenderEnabled = useSelector(featureToggleSelectors.isOPLAutoSelectLenderEnabled);
    const isOnePaySwitchCapReductionCashEnabled = useSelector(featureToggleSelectors.isOnePaySwitchCapReductionCashEnabled);
    const isEquityAllocationOrderEnabled = useSelector(featureToggleSelectors.isEquityAllocationOrderEnabled);
    const leaseCapReductionCash = useSelector((state: StateTree) => offerReduxSelectors.getNetCashDown(state, LEASE));

    const isAutoSelectProgramSelected = currentCalcEndpoint === programQuotes;

    const handleFrequencyChange: InterstateOnChangeCallback<RadioButtonEventValue> = (e) => {
        const freqValue = e.target.value as PaymentScheduleTypes;
        const isOnePayLeaseSwitch = freqValue === PaymentScheduleTypes.OnePayLease;

        // When switching to One Pay Lease and feature is enabled, skip program override, to allow backend to search across all lenders for OPL-supporting programs
        const shouldSkipProgramOverride = isOnePayLeaseSwitch && isOPLAutoSelectLenderEnabled && isAutoSelectProgramSelected;
        const options = shouldSkipProgramOverride ? { skipProgramOverride: true } : undefined;

        const sendsCapReductionCash =
            isOnePayLeaseSwitch && isOnePaySwitchCapReductionCashEnabled && !isEquityAllocationOrderEnabled;
        const downPayment = sendsCapReductionCash ? leaseCapReductionCash : null;

        dispatch(paymentActionCreators.updatePaymentFrequency(LEASE, freqValue, downPayment, options));
    };

    // Current requirement to disable paymentGrid for OnePayLease feature implementation
    const options = PAYMENT_FREQUENCY_OPTIONS.map((option) => ({
        ...option,
        disabled: option.value === PaymentScheduleTypes.OnePayLease && paymentGridEnabled,
        ...(isLocked && { disabled: true, checkedDisabled: true })
    }));

    return {
        label: PAYMENT_FREQUENCY_LABEL,
        radioProps: {
            'data-testid': kebabCase('payment-frequency-radio'),
            name: PAYMENT_FREQUENCY,
            value: paymentFrequency,
            onChange: handleFrequencyChange,
            options
        }
    };
};
